/**
 * Version Check Service
 * 
 * Periodically checks the API version and compares it with the local frontend version.
 * If versions don't match, it notifies the application to display a version mismatch warning.
 * 
 * @author autodata-frontend
 * @date 2025-11-07
 */

import axios from 'axios';
import Utils from './Utils';

export interface VersionCheckConfig {
  onVersionMismatch: (localVersion: string, apiVersion: string) => void;
  onVersionFetched?: (apiVersion: string) => void;
  checkIntervalMs?: number; // Default: 3600000 (1 hour)
}

export class VersionCheckService {
  private intervalId: NodeJS.Timeout | null = null;
  private config: VersionCheckConfig;
  private checkIntervalMs: number;
  private isChecking = false;

  constructor(config: VersionCheckConfig) {
    this.config = config;
    this.checkIntervalMs = config.checkIntervalMs || 3600000; // Default: 1 hour
  }

  /**
   * Start the version checking service
   * Performs an immediate check and then schedules periodic checks
   */
  public start(): void {
    if (this.intervalId) {
      Utils.warn('VersionCheckService is already running');
      return;
    }

    // Perform initial check
    this.checkVersion();

    // Schedule periodic checks
    this.intervalId = setInterval(() => {
      this.checkVersion();
    }, this.checkIntervalMs);

    Utils.log(`VersionCheckService started (checking every ${this.checkIntervalMs / 1000 / 60} minutes)`);
  }

  /**
   * Stop the version checking service
   */
  public stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      Utils.log('VersionCheckService stopped');
    }
  }

  /**
   * Manually trigger a version check
   */
  public async checkVersion(): Promise<void> {
    if (this.isChecking) {
      Utils.log('Version check already in progress, skipping...');
      return;
    }

    this.isChecking = true;

    try {
      const apiBaseUrl = `${Utils.URL_ADDRESS}${Utils.WEBAPI_PATH}`;
      const versionEndpoint = `${apiBaseUrl}/v1/version`;

      Utils.log(`Checking version from: ${versionEndpoint}`);

      const response = await axios.get(versionEndpoint, {
        timeout: 10000, // 10 second timeout
        headers: {
          'Content-Type': 'text/plain',
        },
      });

      // API returns plain text version string
      if (response.data) {
        const apiVersion = typeof response.data === 'string' ? response.data.trim() : response.data.version;
        const localVersion = Utils.VERSION;
        
        // Convert version strings to comparable numbers
        const localVersionNum = this.versionToNumber(localVersion);
        const apiVersionNum = this.versionToNumber(apiVersion);
        
        Utils.log(`Version check: Local=${localVersion} (${localVersionNum}), API=${apiVersion} (${apiVersionNum})`);

        this.config.onVersionFetched?.(apiVersion);

        if (localVersionNum < apiVersionNum) {
          Utils.warn(`Version mismatch detected! Local: ${localVersion}, API: ${apiVersion}`);
          this.config.onVersionMismatch(localVersion, apiVersion);
        } else {
          Utils.log('Version check: Versions match');
        }
      } else {
        Utils.warn('Version check: Invalid response format from API', response.data);
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        Utils.error('Version check failed (Axios error):', error.message);
        if (error.response) {
          Utils.error('Response status:', error.response.status);
          Utils.error('Response data:', error.response.data);
        }
      } else {
        Utils.error('Version check failed (Unknown error):', error);
      }
    } finally {
      this.isChecking = false;
    }
  }

  /**
   * Update the check interval
   * @param intervalMs - New interval in milliseconds
   */
  public updateInterval(intervalMs: number): void {
    this.checkIntervalMs = intervalMs;
    if (this.intervalId) {
      this.stop();
      this.start();
    }
  }

  /**
   * Convert version string to number for comparison.
   * Supports two formats:
   *   - New: "YYMMDD.build-patch"  e.g. "260520.1-1"
   *   - Old: "major.minor.patch"   e.g. "3.3.06"
   * @returns Numeric representation of version
   */
  private versionToNumber(versionStr: string): number {
    try {
      const dotParts = versionStr.split('.');

      // New format: "YYMMDD.build-patch" — second segment contains a hyphen
      if (dotParts.length === 2 && dotParts[1].includes('-')) {
        const date = parseInt(dotParts[0], 10);
        const [build, patch] = dotParts[1].split('-').map(p => parseInt(p, 10));
        return date * 1000000 + (build || 0) * 1000 + (patch || 0);
      }

      // Old format: "major.minor.patch" or shorter variants
      const parts = dotParts.map(part => parseInt(part, 10));
      if (parts.length === 3) {
        return parts[0] * 1000000 + parts[1] * 1000 + parts[2];
      } else if (parts.length === 2) {
        return parts[0] * 1000000 + parts[1] * 1000;
      } else if (parts.length === 1) {
        return parts[0] * 1000000;
      }

      Utils.warn(`Unexpected version format: ${versionStr}`);
      return 0;
    } catch (error) {
      Utils.error(`Failed to parse version string: ${versionStr}`, error);
      return 0;
    }
  }
}
